import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Crown, ArrowLeft, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const PLANS = [
  {
    key: "pro_monthly",
    name: "Pro Monthly",
    price: "$6.99",
    interval: "/mo",
    highlight: false,
    checkoutUrl: "https://square.link/u/Z50nKkSR",
    features: [
      "Unlimited saved workouts",
      "Full history across devices",
      "Draw Checker + mirror substitution",
      "Daily workout email",
      "Cancel anytime",
    ],
  },
  {
    key: "pro_yearly",
    name: "Pro Yearly",
    price: "$49",
    interval: "/yr",
    highlight: true,
    badge: "Best Value · ~41% off",
    checkoutUrl: "https://square.link/u/5YffnYyV",
    features: [
      "Everything in Pro Monthly",
      "Save $34/year vs monthly",
      "Priority support",
    ],
  },
  {
    key: "lifetime",
    name: "Lifetime",
    price: "$79",
    interval: "one-time",
    highlight: false,
    badge: "No renewals",
    checkoutUrl: "https://square.link/u/FX30jryJ",
    features: [
      "Everything in Pro — forever",
      "No subscription, pay once",
      "Future features included",
    ],
  },
];

export default function Upgrade() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);
  const navigate = useNavigate();

  const buy = (plan) => {
    if (!user) {
      navigate("/login", { state: { next: "/upgrade" } });
      return;
    }
    setLoading(plan.key);
    // Square hosted checkout — opens in a new tab so the user keeps our app.
    window.open(plan.checkoutUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => setLoading(null), 1500);
  };

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-100 py-10 px-4">
      <div className="max-w-5xl mx-auto space-y-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300 transition-colors font-mono-digit"
          data-testid="back-to-app"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Hit Path
        </Link>

        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 text-amber-400 font-mono-digit text-xs uppercase tracking-[0.3em]">
            <Crown className="w-3.5 h-3.5" />
            Upgrade to Pro
          </div>
          <h1 className="font-heading text-3xl sm:text-5xl font-extrabold text-slate-50">
            Save every workout. Chase every state.
          </h1>
          <p className="text-slate-400 max-w-xl mx-auto">
            The free workout runs forever. Pro unlocks history across devices, email digests, and
            future features — one price for every game you play.
          </p>
          {user?.plan && user.plan !== "free" && (
            <div
              data-testid="current-plan-badge"
              className="inline-flex items-center gap-2 text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 rounded-full px-4 py-1.5 text-sm font-mono-digit"
            >
              <Sparkles className="w-4 h-4" />
              Current plan: {user.plan.toUpperCase()}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plans-grid">
          {PLANS.map((plan) => (
            <Card
              key={plan.key}
              data-testid={`plan-card-${plan.key}`}
              className={`${
                plan.highlight
                  ? "bg-gradient-to-br from-amber-500/15 to-amber-500/5 border-amber-500/60 shadow-[0_0_32px_rgba(245,158,11,0.2)]"
                  : "bg-[#131c2e] border-slate-800"
              } relative overflow-visible`}
            >
              {plan.badge && (
                <div
                  className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-heading uppercase tracking-widest font-bold ${
                    plan.highlight ? "bg-amber-500 text-slate-950" : "bg-slate-800 text-amber-300"
                  }`}
                >
                  {plan.badge}
                </div>
              )}
              <CardContent className="p-6 space-y-5 pt-8">
                <div>
                  <h3 className="font-heading text-xl font-bold text-slate-50">{plan.name}</h3>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="font-mono-digit text-4xl font-extrabold text-slate-50">
                      {plan.price}
                    </span>
                    <span className="text-sm text-slate-500">{plan.interval}</span>
                  </div>
                </div>
                <ul className="space-y-2.5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`buy-${plan.key}`}
                  onClick={() => buy(plan)}
                  disabled={loading === plan.key}
                  className={`w-full h-12 font-heading font-bold ${
                    plan.highlight
                      ? "bg-amber-500 hover:bg-amber-400 text-slate-950"
                      : "bg-slate-800 hover:bg-slate-700 text-slate-100"
                  }`}
                >
                  {loading === plan.key ? "…" : "Continue"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <p className="text-center text-xs text-slate-500 max-w-xl mx-auto" data-testid="payment-footer">
          Secure checkout by Square. Billed by The Jersey Fellas LLC. After payment, email{" "}
          <a href="mailto:support@thejerseyfellasllc.com" className="text-amber-300 hover:text-amber-200 underline">
            support@thejerseyfellasllc.com
          </a>{" "}
          with your receipt and we&apos;ll activate Pro on your account within one business day.
        </p>
      </div>
    </div>
  );
}
